import { useState, useEffect } from "react";
import {
  collection,
  query,
  where,
  getDocs,
  limit,
  orderBy,
} from "firebase/firestore";
import { parseISO, formatDistanceToNowStrict } from "date-fns";
import { db } from "../../utils/firebase";

import { MdRefresh } from 'react-icons/md';
import BillModal from '../UserDashboard/BillModel';
import KOTModal from '../UserDashboard/KOTModal';


const TodaysCompletedOrders = () => {
  const [groupedOrders, setGroupedOrders] = useState({});
  const [loadingCompleted, setLoadingCompleted] = useState(false);
  const restaurantEmail = localStorage.getItem("user");
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isKOTOpen, setIsKOTOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const fetchCompletedOrdersToday = async () => {
    setLoadingCompleted(true);
    try {
      const now = new Date();
      const IST_OFFSET_MINUTES = 330;

      const startIST = new Date(now);
      startIST.setUTCMinutes(startIST.getUTCMinutes() - IST_OFFSET_MINUTES);
      startIST.setUTCHours(0, 0, 0, 0);

      const endIST = new Date(startIST);
      endIST.setUTCHours(23, 59, 59, 999);

      const startISO = startIST.toISOString();
      const endISO = endIST.toISOString();

      const ordersRef = collection(db, "orders");
      const q = query(
        ordersRef,
        where("email", "==", restaurantEmail),
        where("createdDateTime", ">=", startISO),
        where("createdDateTime", "<=", endISO),
        where("isBilled", "==", false),
        orderBy('createdDateTime', 'desc'),
        limit(20)
      );

      const querySnapshot = await getDocs(q);
      let rawOrders = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Limit to last 50 orders by createdDateTime descending
      rawOrders.sort((a, b) => new Date(b.createdDateTime) - new Date(a.createdDateTime));


      const grouped = {};

      rawOrders.forEach((order) => {

        const orderType = order.orderType;   // Already normalized
        const tableNumber = order.tableNumber;
        const roomNumber = order.roomNumber;
        const phone = order.customerPhone;
        const customerName = order.customerName;
        const address = order.address;
        const isBilled = order.isBilled || false;

        // Extract items & total only for display
        const parts = order.order?.split("|") || [];
        const itemsRaw = parts[0]?.replace("Items:", "").trim();
        const total = parts.find((p) => p.includes("Total:"))?.match(/₹(\d+)/)?.[1];

        let groupKey = "";

        if (orderType === "DELIVERY") {
          groupKey = `Delivery|${phone}|${customerName}`;
        }
        else if (orderType === "DINE_IN") {
          groupKey = `Table ${tableNumber}|${phone}|${customerName}`;
        }
        else if (orderType === "TAKEAWAY") {
          groupKey = `Takeaway|${phone}|${customerName}`;
        }
        else if (orderType === "ROOM") {
          groupKey = `Room ${roomNumber}|${phone}|${customerName}`;
        }
        else {
          groupKey = `Other|${phone}|${customerName}`;
        }

        if (!grouped[groupKey]) {
          grouped[groupKey] = {
            orderType,
            phone,
            customerName,
            tableNumber: tableNumber ?? null,
            roomNumber: roomNumber ?? null,
            address: address ?? null,
            orders: [],
            totalBill: 0,
            isFullyBilled: true
          };
        }

        grouped[groupKey].orders.push({
          ...order,
          items: itemsRaw,
          createdDateTime: parseISO(order.createdDateTime),
        });

        if (!isBilled) {
          grouped[groupKey].isFullyBilled = false;
        }

        grouped[groupKey].totalBill += total ? parseInt(total) : 0;
      });



      setGroupedOrders(grouped);
    } catch (error) {
      console.error("Error fetching completed orders:", error);
    } finally {
      setLoadingCompleted(false);
    }
  };

  useEffect(() => {
    if (restaurantEmail) {
      fetchCompletedOrdersToday();
    }
  }, [restaurantEmail]);

  const renderGroup = (groupKey, group) => (
    <div key={groupKey} className="border border-gray-200 rounded-lg p-3 mb-4 bg-white shadow-sm text-sm">
      <div className="flex justify-between items-center mb-1">
        <h3 className="font-semibold text-gray-800 text-sm">
          {group.orderType === "DELIVERY"
            ? "Delivery Order"
            : group.orderType === "DINE_IN"
              ? `Table #${group.tableNumber}`
              : group.orderType === "TAKEAWAY"
                ? "In-Store Order"
                : group.orderType === "ROOM"
                  ? `Room #${group.roomNumber}`
                  : "Order"}

        </h3>
        <span className="text-gray-600 text-xs">
          {group.customerName} ({group.phone})
        </span>
      </div>

     {group.orders.map((order, index) => (
  <div key={order.id} className="mb-3 border-b pb-2">

    <div className="flex justify-between items-center">
      <div className="text-xs text-gray-700 font-medium">
        Order {index + 1} - {formatDistanceToNowStrict(order.createdDateTime, { addSuffix: true })}
      </div>

      <button
        onClick={() => {
          setSelectedOrder({
            ...order,
            tableNumber: group.tableNumber,
            roomNumber: group.roomNumber,
            orderType: group.orderType,
            customerName: group.customerName
          });
          setIsKOTOpen(true);
        }}
        className="px-2 py-1 text-xs bg-orange-600 text-white rounded hover:bg-orange-700"
      >
        KOT
      </button>
    </div>

    <div className="text-xs text-gray-600 mt-1">
      {order.items}
    </div>

  </div>
))}

      {group.isDelivery && group.address && (
        <div className="mt-2 text-xs text-gray-700">
          <span className="font-medium">Address:</span> {group.address}
        </div>
      )}

      <div className="mt-2 font-semibold text-green-700 text-right text-sm">
        Total: ₹{group.totalBill}
      </div>
    <div className="flex gap-2 mt-2">
  <button
    onClick={() => {
      setSelectedGroup(group);
      setIsModalOpen(true);
    }}
    className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
  >
    Bill
  </button>
 
</div>

    </div>
  );

  const groupedArray = Object.entries(groupedOrders);

  return (
    <div id="print-root" className="w-full bg-white border border-gray-100 rounded-2xl shadow-md p-4">
      <div className="flex justify-between items-center mb-3">
        <h2 className="text-md font-bold text-gray-800">📊 Today's Orders</h2>
        <button
          onClick={fetchCompletedOrdersToday}
          className="text-gray-500 hover:text-gray-800"
          title="Refresh orders"
        >
          Refresh <MdRefresh size={18} className="inline-block" />
        </button>
      </div>

      {loadingCompleted ? (
        <div className="text-gray-400 text-sm">Loading...</div>
      ) : groupedArray.length === 0 ? (
        <div className="text-gray-400 text-sm">No orders today.</div>
      ) : (
        <div className="max-h-[800px] overflow-y-auto pr-1">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Dine-in Orders */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">🛒 Orders</h3>
              {groupedArray
                .filter(([_, group]) => group.orderType !== "DELIVERY")
                .map(([key, group]) => renderGroup(key, group))}
            </div>

            {/* Delivery Orders */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">🏍️ Delivery Orders</h3>
              {groupedArray
                .filter(([_, group]) => group.orderType === "DELIVERY")
                .map(([key, group]) => renderGroup(key, group))}
            </div>
          </div>
        </div>
      )}

      <BillModal
  isOpen={isModalOpen}
  onClose={() => setIsModalOpen(false)}
  group={selectedGroup}
  onBillingSuccess={fetchCompletedOrdersToday}
/>
<KOTModal
  isOpen={isKOTOpen}
  onClose={() => setIsKOTOpen(false)}
  order={selectedOrder}
/>


    </div>
  );
};

export default TodaysCompletedOrders;
